import React from 'react'
import PropTypes from 'prop-types'

import { getAverageBias, calculateBiasForLevel, isQcComplete, getCV, countOutOfRange, areAllInitialJobsComplete, countTotalOutOfRange } from '../../../store/qc'
import _ from 'lodash'

const BIAS_TARGET = 7.5
const CV_TARGET = 10

class StatsView extends React.Component {
  static propTypes = {
    qcResults: PropTypes.shape({
      low_jobs: PropTypes.arrayOf(PropTypes.shape({
        job_id: PropTypes.number
      })),
      normal_jobs: PropTypes.arrayOf(PropTypes.shape({
        job_id: PropTypes.number
      })),
      high_jobs: PropTypes.arrayOf(PropTypes.shape({
        job_id: PropTypes.number
      }))
    }),
    controlMaterial: PropTypes.shape({
      low: PropTypes.shape({}),
      normal: PropTypes.shape({}),
      high: PropTypes.shape({})
    }),
    showOnlyStatus: PropTypes.bool,
    showOnlyStatistics: PropTypes.bool
  }

  static defaultProps = {
    qcResults: {
      low_jobs: [],
      normal_jobs: [],
      high_jobs: []
    },
    controlMaterial: {
      low: {},
      normal: {},
      high: {}
    }
  }


  render () {
    const { qcResults, controlMaterial } = this.props
    const isQcFinished = isQcComplete(qcResults)
    
    // Enhanced completion logic
    const allInitialJobsComplete = areAllInitialJobsComplete(qcResults)
    const totalOutOfRange = controlMaterial ? countTotalOutOfRange(qcResults, controlMaterial) : 0
    
    // Check if retest/Step 2 tests are complete
    let testingComplete = false
    let testResult = null
    let testMessage = 'Test Not Complete'
    
    if (!allInitialJobsComplete) {
      testMessage = 'Test Not Complete: Waiting for initial test strips to finish'
    } else if (totalOutOfRange === 0) {
      testingComplete = true
      testResult = 'passed'
    } else if (totalOutOfRange >= 3) {
      testingComplete = true  
      testResult = 'failed'
      testMessage = 'LVT Failed'
    } else if (totalOutOfRange === 2) {
      // Check Step 2 completion
      const allStep2Complete = ['low', 'normal', 'high'].every(level => {
        const jobs = qcResults[`${level}_jobs`] || [];
        const step2Jobs = jobs.slice(3, 6);
        return step2Jobs.length >= 3 && step2Jobs.every(job => 
          job && job.wbc_count && typeof job.wbc_count === 'number'
        );
      });
      
      if (!allStep2Complete) {
        testMessage = 'Test Not Complete: Test 2 expanded sample testing required'
      } else {
        testingComplete = true
        // Step 2 protocol: Count failures in additional 9 strips (d2)
        const d2 = ['low', 'normal', 'high'].reduce((total, level) => {
          const jobs = qcResults[`${level}_jobs`] || [];
          const step2Jobs = jobs.slice(3, 6); // Jobs 4-6 for each level
          const levelMaterial = controlMaterial[level];
          return total + countOutOfRange(step2Jobs, levelMaterial);
        }, 0);
        
        if (d2 === 0) {
          testResult = 'passed' // No additional failures - accept lot
        } else {
          testResult = 'failed' // One or more failures - quarantine
          testMessage =  `Test 2 Failed. LVT Failed`
        }
      }
    } else if (totalOutOfRange === 1) {
      // Find which level failed and check retests
      const failedLevel = ['low', 'normal', 'high'].find(level => {
        const jobs = qcResults[`${level}_jobs`] || [];
        const initialJobs = jobs.slice(0, 3);
        return countOutOfRange(initialJobs, controlMaterial[level]) > 0;
      });
      
      if (failedLevel) {
        const jobs = qcResults[`${failedLevel}_jobs`] || [];
        const retestJobs = jobs.slice(3, 6);
        const completedRetests = retestJobs.filter(job => 
          job && job.wbc_count && typeof job.wbc_count === 'number'
        );
        
        if (completedRetests.length < 3) {
          testMessage = `Test Not Complete: Retest required for ${failedLevel} level`
        } else {
          const retestsPassed = completedRetests.every(job =>
            job.wbc_count > controlMaterial[failedLevel].wbc_min && 
            job.wbc_count < controlMaterial[failedLevel].wbc_max
          );
          
          if (retestsPassed) {
            testingComplete = true
            testResult = 'passed'
          } else {
            // Retest failed, need to check Step 2 completion
            const step2JobRange = failedLevel === 'low' || failedLevel === 'normal' || failedLevel === 'high' ? 
              { failingLevel: failedLevel } : null;
            
            if (step2JobRange) {
              // Check Step 2 completion for all levels with appropriate job ranges
              const allStep2Complete = ['low', 'normal', 'high'].every(level => {
                const levelJobs = qcResults[`${level}_jobs`] || [];
                // Failing level uses jobs 7-9, others use jobs 4-6
                const step2Jobs = level === failedLevel ? levelJobs.slice(6, 9) : levelJobs.slice(3, 6);
                return step2Jobs.length === 3 && step2Jobs.every(job => 
                  job && job.wbc_count && typeof job.wbc_count === 'number'
                );
              });
              
              
              if (!allStep2Complete) {
                testMessage = `Test Not Complete: Test 2 Expanded Sample required`
              } else {
                // Step 2 complete, check results
                testingComplete = true
                // Use same logic as ResultsView for consistency
                const step2Failures = ['low', 'normal', 'high'].reduce((total, level) => {
                  const levelJobs = qcResults[`${level}_jobs`] || [];
                  // Each level uses different job ranges based on whether it was the failing level
                  const step2Jobs = level === failedLevel ? levelJobs.slice(6, 9) : levelJobs.slice(3, 6);
                  const levelMaterial = controlMaterial[level];
                  return total + step2Jobs.filter(job =>
                    job && typeof job.wbc_count === 'number' &&
                    (job.wbc_count <= levelMaterial.wbc_min || job.wbc_count >= levelMaterial.wbc_max)
                  ).length;
                }, 0);
                
                testResult = step2Failures === 0 ? 'passed' : 'failed'
                testMessage = step2Failures === 0 ? 
                  `Test 2 Complete. No additional failures in expanded sample. LVT Pass.` :
                  `Test 2 Failed. LVT Failed.`
              }
            } else {
              testingComplete = true
              testResult = 'failed'
            }
          }
        }
      }
    }
    let lowBias = _.round(calculateBiasForLevel(qcResults, 'low', controlMaterial) * 100, 1)
    lowBias = _.isNaN(lowBias) ? 'N/A' : lowBias
    let normalBias = _.round(calculateBiasForLevel(qcResults, 'normal', controlMaterial) * 100, 1)
    normalBias = _.isNaN(normalBias) ? 'N/A' : normalBias
    let highBias = _.round(calculateBiasForLevel(qcResults, 'high', controlMaterial) * 100, 1)
    highBias = _.isNaN(highBias) ? 'N/A' : highBias

    let averageBias = _.round(getAverageBias(qcResults, controlMaterial) * 100, 1) // multiply by 100 to get a percentage
    if (_.isNaN(averageBias)) {
      averageBias = 0
    }
    let lowJobsCV = _.round(getCV(qcResults, 'low') * 100, 1)
    let normalJobsCV = _.round(getCV(qcResults, 'normal') * 100, 1)
    let highJobsCV = _.round(getCV(qcResults, 'high') * 100, 1)

    const passed = averageBias < BIAS_TARGET &&
      lowJobsCV < CV_TARGET &&
      normalJobsCV < CV_TARGET &&
      highJobsCV < CV_TARGET

    // Helper to check if all jobs for a level are in range
    function areAllJobsInRange(jobs, levelMaterial) {
      return _.every(jobs, (job) => {
        if (!job || typeof job.wbc_count !== 'number') return false
        return job.wbc_count >= levelMaterial.wbc_min && job.wbc_count <= levelMaterial.wbc_max
      })
    }

    // Check all levels for WBC in range
    const lowInRange = areAllJobsInRange(qcResults.low_jobs, controlMaterial.low)
    const normalInRange = areAllJobsInRange(qcResults.normal_jobs, controlMaterial.normal)
    const highInRange = areAllJobsInRange(qcResults.high_jobs, controlMaterial.high)
    const allWbcInRange = lowInRange && normalInRange && highInRange

    const { showOnlyStatus, showOnlyStatistics } = this.props;

    return (
      <div style={{ paddingTop: 20 }} className='html2pdf-break-before'>
        {/* Status section - show unless showOnlyStatistics is true */}
        {!showOnlyStatistics && (
          <div>
            { !testingComplete ? (
              <div className='alert alert-warning' role='alert'>
                {testMessage}
              </div>
            ) : (
              <div>
                {testResult === 'passed' 
                  ? <div className='alert alert-success' role='alert'>LVT {qcResults.test_strip_lot} Passed</div>
                  : testResult === 'quarantined'
                  ? <div className='alert alert-danger' role='alert'>LVT {qcResults.test_strip_lot} Failed: {testMessage}</div>
                  : <div className='alert alert-danger' role='alert'>LVT {qcResults.test_strip_lot} Failed: One or more tests failed</div>
                }
              </div>
            )}
          </div>
        )}
        {/* Statistics section - show unless showOnlyStatus is true */}
        {!showOnlyStatus && (
          <div style={{ marginTop: 32 }}>
            <h5>Result Statistics</h5>
          <table className='table table-bordered'>
            <thead className='thead-light'>
              <tr>
                <th scope='col'>Statistic</th>
                <th scope='col'>Value</th>
                <th scope='col'>Target</th>
                <th scope='col'>Passed</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Low Bias</td>
                <td>{lowBias}%</td>
                <td>-</td>
                <td>-</td>
              </tr>

              <tr>
                <td>Normal Bias</td>
                <td>{normalBias}%</td>
                <td>-</td>
                <td>-</td>
              </tr>
              <tr>
                <td>High Bias</td>
                <td>{highBias}%</td>
                <td>-</td>
                <td>-</td>
              </tr>
              <tr>
                <td>Average Bias</td>
                <td>{averageBias}%</td>
                <td>{BIAS_TARGET}%</td>
                <td>{averageBias < BIAS_TARGET ? 'Yes' : 'No'}</td>
              </tr>
              <tr>
                <td>Low CV</td>
                <td>{lowJobsCV}%</td>
                <td>{CV_TARGET}%</td>
                <td>{lowJobsCV < CV_TARGET ? 'Yes' : 'No'}</td>
              </tr>

              <tr>
                <td>Normal CV</td>
                <td>{normalJobsCV}%</td>
                <td>{CV_TARGET}%</td>
                <td>{normalJobsCV < CV_TARGET ? 'Yes' : 'No'}</td>
              </tr>

              <tr>
                <td>High CV</td>
                <td>{highJobsCV}%</td>
                <td>{CV_TARGET}%</td>
                <td>{highJobsCV < CV_TARGET ? 'Yes' : 'No'}</td>
              </tr>
              {/* New WBC in range summary row */}
              <tr>
                <td>All WBC Values In Range</td>
                <td colSpan={2}>-</td>
                <td>{allWbcInRange ? 'Yes' : 'No'}</td>
              </tr>
            </tbody>
          </table>
          </div>
        )}
      </div>
    )
  }
}

export default StatsView



// WEBPACK FOOTER //
// ./src/routes/QC/components/StatsView.jsx